// Interior Story letterhead — the exact studio letterhead (Proposals.jpg) is drawn
// as a fixed full-page background on every page; content is typeset in Open Sans
// in the area right of the sidebar. Used for both the Proposal (page-builder
// blocks) and the Invoice (fixed layout, no terms).
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  LETTERHEAD_BG, OPEN_SANS_REGULAR, OPEN_SANS_BOLD, OPEN_SANS_SEMIBOLD,
  OPEN_SANS_ITALIC, OPEN_SANS_BOLDITALIC,
} from '@/lib/letterheadAssets';

const GOLD = [242, 166, 29];
const DARK = [40, 40, 44];
const GREY_LINE = [200, 200, 200];

const PAGE_W = 210, PAGE_H = 297;
const PT = 0.3528;             // pt → mm

// Content-area geometry. Mutable so per-document margins can adjust it;
// applyMargins() is called at the start of every generate*.
export const DEFAULT_MARGINS = { left: 73, right: 13, top: 24, bottom: 31 };
let CX = 73, CR = 197, CW = 124, TOP = 24, BOTTOM = 266;

function applyMargins(m) {
  const mm = { ...DEFAULT_MARGINS, ...(m || {}) };
  CX = Math.min(Math.max(Number(mm.left) || DEFAULT_MARGINS.left, 0), 150);
  CR = PAGE_W - Math.min(Math.max(Number(mm.right) || DEFAULT_MARGINS.right, 0), 80);
  if (CR - CX < 40) CR = CX + 40;
  CW = CR - CX;
  TOP = Math.min(Math.max(Number(mm.top) || DEFAULT_MARGINS.top, 6), 120);
  BOTTOM = PAGE_H - Math.min(Math.max(Number(mm.bottom) || DEFAULT_MARGINS.bottom, 10), 100);
}

function money(n) {
  const v = Number(n) || 0;
  return 'Rs ' + v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function registerFonts(doc) {
  doc.addFileToVFS('OpenSans-Regular.ttf', OPEN_SANS_REGULAR);
  doc.addFont('OpenSans-Regular.ttf', 'OpenSans', 'normal');
  doc.addFileToVFS('OpenSans-Bold.ttf', OPEN_SANS_BOLD);
  doc.addFont('OpenSans-Bold.ttf', 'OpenSans', 'bold');
  doc.addFileToVFS('OpenSans-Semibold.ttf', OPEN_SANS_SEMIBOLD);
  doc.addFont('OpenSans-Semibold.ttf', 'OpenSans', 'semibold');
  doc.addFileToVFS('OpenSans-Italic.ttf', OPEN_SANS_ITALIC);
  doc.addFont('OpenSans-Italic.ttf', 'OpenSans', 'italic');
  doc.addFileToVFS('OpenSans-BoldItalic.ttf', OPEN_SANS_BOLDITALIC);
  doc.addFont('OpenSans-BoldItalic.ttf', 'OpenSans', 'bolditalic');
}

// The letterhead artwork fills the whole page; the alias makes jsPDF embed the
// JPEG once and reuse it on every page.
function drawChrome(doc) {
  doc.addImage(LETTERHEAD_BG, 'JPEG', 0, 0, PAGE_W, PAGE_H, 'letterheadBg', 'FAST');
}

// Simple flowing content cursor with pagination (re-draws the letterhead on new pages).
function makeFlow(doc) {
  let y = TOP;
  const newPage = () => { doc.addPage(); drawChrome(doc); y = TOP; };
  const lh = (size) => size * PT * 1.45;
  return {
    get y() { return y; }, set y(v) { y = v; },
    newPage,
    ensure(h) { if (y + h > BOTTOM) newPage(); },
    line(color = GOLD) {
      this.ensure(6);
      doc.setDrawColor(...color); doc.setLineWidth(0.7); doc.line(CX, y, CR, y);
      y += 6;
    },
    para(text, { size = 10, color = DARK, font = 'normal', gap = 4, indent = 0, align } = {}) {
      if (text == null || text === '') { y += gap; return; }
      doc.setTextColor(...color); doc.setFont('OpenSans', font); doc.setFontSize(size);
      const lines = doc.splitTextToSize(String(text), CW - indent);
      const h = lines.length * lh(size);
      this.ensure(h);
      if (align === 'right') doc.text(lines, CR, y, { align: 'right', lineHeightFactor: 1.45 });
      else doc.text(lines, CX + indent, y, { lineHeightFactor: 1.45 });
      y += h + gap;
    },
    bullets(items, { size = 10, gap = 2.2 } = {}) {
      doc.setTextColor(...DARK); doc.setFont('OpenSans', 'normal'); doc.setFontSize(size);
      for (const it of items) {
        const lines = doc.splitTextToSize(String(it), CW - 6);
        const h = lines.length * lh(size);
        this.ensure(h + gap);
        doc.text('•', CX + 1, y);
        doc.text(lines, CX + 6, y, { lineHeightFactor: 1.45 });
        y += h + gap;
      }
    },
    heading(text) {
      this.ensure(18);
      doc.setTextColor(...GOLD); doc.setFont('OpenSans', 'bold'); doc.setFontSize(12);
      doc.text((text || '').toUpperCase(), CX, y);
      y += 2.5;
      doc.setDrawColor(...GOLD); doc.setLineWidth(0.7); doc.line(CX, y, CR, y);
      y += 7;
    },
    space(h) { y += h; },
  };
}

// ---------------------------------------------------------------------------
// Rich-text (limited HTML) rendering: b/strong, i/em, u, font sizes
// (span.fs-N / font[size] / inline font-size), p/div paragraphs with
// text-align, ul/ol lists. Produced by the canvas contentEditable blocks.
// ---------------------------------------------------------------------------

const FONT_TAG_SIZES = { 1: 7.5, 2: 9, 3: 10, 4: 12, 5: 14, 6: 18, 7: 24 };

function runStyleName(r) {
  return r.bold && r.italic ? 'bolditalic' : r.bold ? 'bold' : r.italic ? 'italic' : 'normal';
}

function collectRuns(node, style, runs) {
  node.childNodes.forEach((ch) => {
    if (ch.nodeType === 3) { if (ch.textContent) runs.push({ text: ch.textContent, ...style }); return; }
    if (ch.nodeType !== 1) return;
    const tag = ch.tagName.toLowerCase();
    if (tag === 'br') { runs.push({ text: '\n', ...style }); return; }
    const s = { ...style };
    if (tag === 'b' || tag === 'strong') s.bold = true;
    if (tag === 'i' || tag === 'em') s.italic = true;
    if (tag === 'u') s.underline = true;
    if (tag === 'font' && ch.getAttribute('size')) s.size = FONT_TAG_SIZES[ch.getAttribute('size')] || s.size;
    const cls = ch.getAttribute && (ch.getAttribute('class') || '');
    const fsCls = /(?:^|\s)fs-(\d+(?:\.\d+)?)(?:\s|$)/.exec(cls);
    if (fsCls) s.size = parseFloat(fsCls[1]);
    const st = ch.style || {};
    if (st.fontSize) {
      const v = parseFloat(st.fontSize);
      if (v) s.size = st.fontSize.endsWith('px') ? v * 0.75 : v;
    }
    if (st.fontWeight === 'bold' || Number(st.fontWeight) >= 600) s.bold = true;
    if (st.fontStyle === 'italic') s.italic = true;
    if ((st.textDecoration || st.textDecorationLine || '').includes('underline')) s.underline = true;
    collectRuns(ch, s, runs);
  });
}

// Greedy word-wrap of styled runs into lines, then draw with per-run fonts.
// anchorTop: flow.y tracks the line TOP (box mode) instead of the baseline.
function drawRuns(doc, flow, runs, { x, width, align, gap = 2, anchorTop = false }) {
  const words = [];
  runs.forEach((r) => {
    String(r.text).split(/(\n)|(\s+)/).forEach((w) => { if (w) words.push({ ...r, text: w }); });
  });
  const setF = (r) => { doc.setFont('OpenSans', runStyleName(r)); doc.setFontSize(r.size); };
  const lines = [];
  let line = [], lw = 0;
  const flush = () => { lines.push({ segs: line, w: lw }); line = []; lw = 0; };
  for (const w of words) {
    if (w.text === '\n') { flush(); continue; }
    setF(w);
    const ww = doc.getTextWidth(w.text);
    if (lw + ww > width && line.length && w.text.trim() !== '') flush();
    if (line.length === 0 && w.text.trim() === '') continue; // no leading spaces
    line.push({ ...w, w: ww }); lw += ww;
  }
  if (line.length) flush();
  if (!lines.length) { flow.space(gap); return; }
  for (const ln of lines) {
    const maxSize = ln.segs.length ? Math.max(...ln.segs.map(s => s.size)) : 10;
    const h = maxSize * PT * 1.45;
    flow.ensure(h);
    let cx = x;
    if (align === 'center') cx = x + Math.max(0, (width - ln.w) / 2);
    else if (align === 'right') cx = x + Math.max(0, width - ln.w);
    // baseline sits ~1.03em below the line top (matches the canvas line box)
    const base = anchorTop ? flow.y + maxSize * PT * 1.03 : flow.y;
    for (const s of ln.segs) {
      setF(s); doc.setTextColor(...DARK);
      doc.text(s.text, cx, base);
      if (s.underline && s.text.trim() !== '') {
        doc.setDrawColor(...DARK); doc.setLineWidth(0.25);
        doc.line(cx, base + 0.9, cx + s.w, base + 0.9);
      }
      cx += s.w;
    }
    flow.y += h;
  }
  flow.space(gap);
}

function elAlign(el) {
  const a = (el.style && el.style.textAlign) || el.getAttribute('align') || '';
  return a === 'center' || a === 'right' ? a : 'left';
}

function renderRichHtml(doc, flow, html, opts = {}) {
  if (!html) return;
  const { x = CX, width = CW, anchorTop = false, baseSize = 10 } = opts;
  const body = new DOMParser().parseFromString(`<div id="r">${html}</div>`, 'text/html').getElementById('r');
  const style = { bold: false, italic: false, underline: false, size: baseSize };
  let inlineGroup = [];
  const flushInline = () => {
    if (!inlineGroup.length) return;
    drawRuns(doc, flow, inlineGroup, { x, width, align: 'left', anchorTop });
    inlineGroup = [];
  };
  const renderList = (el, ordered) => {
    let n = 1;
    el.childNodes.forEach((li) => {
      if (li.nodeType !== 1 || li.tagName.toLowerCase() !== 'li') return;
      const runs = [];
      collectRuns(li, style, runs);
      const marker = ordered ? `${n}.` : '•';
      n += 1;
      const mSize = baseSize;
      flow.ensure(mSize * PT * 1.45);
      doc.setFont('OpenSans', 'normal'); doc.setFontSize(mSize); doc.setTextColor(...DARK);
      doc.text(marker, x + 1, anchorTop ? flow.y + mSize * PT * 1.03 : flow.y);
      drawRuns(doc, flow, runs, { x: x + 6, width: width - 6, align: 'left', gap: 1, anchorTop });
    });
    flow.space(1.5);
  };
  body.childNodes.forEach((ch) => {
    if (ch.nodeType === 3) {
      if (ch.textContent.trim()) inlineGroup.push({ text: ch.textContent, ...style });
      return;
    }
    if (ch.nodeType !== 1) return;
    const tag = ch.tagName.toLowerCase();
    if (tag === 'ul' || tag === 'ol') { flushInline(); renderList(ch, tag === 'ol'); return; }
    if (tag === 'p' || tag === 'div' || /^h[1-6]$/.test(tag) || tag === 'blockquote') {
      flushInline();
      const runs = [];
      collectRuns(ch, style, runs);
      // an inner list inside a div (contentEditable quirk)
      const innerList = ch.querySelector && ch.querySelector('ul, ol');
      if (innerList && ch.children.length === 1) { renderList(innerList, innerList.tagName.toLowerCase() === 'ol'); return; }
      drawRuns(doc, flow, runs, { x, width, align: elAlign(ch), anchorTop });
      return;
    }
    if (tag === 'br') { flushInline(); flow.space(4); return; }
    // inline element at top level (e.g. loose <b>…</b>): collect with its own styling
    collectRuns({ childNodes: [ch] }, style, inlineGroup);
  });
  flushInline();
}

// ---------------------------------------------------------------------------

function feeTable(doc, flow, feeRows, { total, gstIncluded, gstRate, gstAmount, subtotal }) {
  const body = (feeRows || []).map(r => [
    r.description || '',
    r.qty != null && r.qty !== '' ? String(r.qty) : '',
    r.rate != null && r.rate !== '' ? String(r.rate) : 'L.S.',
    money(r.price),
  ]);
  const divider = { content: '', styles: { lineWidth: { top: 0.4 }, lineColor: [220, 220, 220] } };
  if (gstIncluded) {
    body.push([divider, '', 'Subtotal', money(subtotal)]);
    body.push(['', '', `GST (${gstRate || 18}%)`, money(gstAmount)]);
    body.push(['', '', { content: 'Total', styles: { fontStyle: 'bold' } }, { content: money(total), styles: { fontStyle: 'bold' } }]);
  } else {
    // No GST → subtotal equals total; show a single Total line.
    body.push([divider, '', { content: 'Total', styles: { fontStyle: 'bold' } }, { content: money(total), styles: { fontStyle: 'bold' } }]);
  }

  autoTable(doc, {
    startY: flow.y,
    margin: { left: CX, right: PAGE_W - CR, top: TOP, bottom: PAGE_H - BOTTOM },
    head: [['DESCRIPTION', 'QTY.', 'RATE', 'PRICE']],
    body,
    theme: 'plain',
    styles: { font: 'OpenSans', fontSize: 10, cellPadding: { top: 2.4, bottom: 2.4, left: 0, right: 2 }, textColor: DARK, valign: 'top' },
    headStyles: { textColor: GOLD, fontStyle: 'bold', fontSize: 11, cellPadding: { top: 1, bottom: 2, left: 0, right: 2 }, lineWidth: { bottom: 0.7 }, lineColor: GOLD },
    columnStyles: { 0: { cellWidth: Math.max(40, CW - 66) }, 1: { cellWidth: 20 }, 2: { cellWidth: 20 }, 3: { halign: 'right' } },
    didDrawPage: (data) => { if (data.pageNumber > 1) drawChrome(doc); },
  });
  flow.y = doc.lastAutoTable.finalY + 8;
}

function header(doc, flow, { title, date, toName, re }) {
  doc.setTextColor(...DARK); doc.setFont('OpenSans', 'normal'); doc.setFontSize(10.5);
  if (date) doc.text(String(date), CR, Math.max(14, TOP - 6), { align: 'right' });
  doc.setFont('OpenSans', 'bold'); doc.setFontSize(22);
  const t0 = Math.max(24, TOP + 6);
  const tlines = doc.splitTextToSize((title || '').toUpperCase(), 90);
  doc.text(tlines, CX, t0);
  flow.y = t0 + tlines.length * 8.5 + 9;
  if (toName || re) {
    doc.setFont('OpenSans', 'semibold'); doc.setFontSize(10.5); doc.setTextColor(...DARK);
    flow.ensure(18);
    doc.text('To,', CX, flow.y); flow.y += 5.5;
    if (toName) { doc.text(String(toName), CX, flow.y); flow.y += 5.5; }
    if (re) { const l = doc.splitTextToSize(`Re: ${re}`, CW); doc.text(l, CX, flow.y, { lineHeightFactor: 1.45 }); flow.y += l.length * 5.5; }
    flow.y += 5;
  }
}

function accountBlock(doc, flow, bank, { heading = 'Account details for payment:' } = {}) {
  flow.para(heading, { font: 'semibold', gap: 1.5 });
  const b = bank || {};
  const line = (label, val) => flow.para(`${label} ${val || ''}`, { gap: 0.8 });
  line('Account Holder:', b.account_holder);
  line('Account Number:', b.account_number);
  line('IFSC:', b.ifsc);
  line('Branch:', b.branch);
  line('Account Type:', b.account_type);
  flow.space(5);
}

function signoff(doc, flow, signName, fallbackName) {
  flow.ensure(16);
  doc.setFont('OpenSans', 'normal'); doc.setFontSize(10.5); doc.setTextColor(...DARK);
  doc.text('Sincerely,', CR, flow.y, { align: 'right' }); flow.y += 5.5;
  doc.setFont('OpenSans', 'semibold');
  doc.text(String(signName || fallbackName || ''), CR, flow.y, { align: 'right' });
}

function newLetterheadDoc() {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  registerFonts(doc);
  drawChrome(doc);
  return doc;
}

// ---------------------------------------------------------------------------
// Free-positioned box rendering (current canvas format): each box carries
// {page, x, y, w} in mm and is drawn top-anchored at its position.
// ---------------------------------------------------------------------------

function headingAt(doc, text, x, y, w) {
  doc.setTextColor(...GOLD); doc.setFont('OpenSans', 'bold'); doc.setFontSize(12);
  doc.text((text || '').toUpperCase(), x, y + 12 * PT * 1.03);
  const uy = y + 12 * PT * 1.45 + 0.8;
  doc.setDrawColor(...GOLD); doc.setLineWidth(0.7); doc.line(x, uy, x + w, uy);
}

function bankAt(doc, x, y, bank) {
  const b = bank || {};
  let cy = y + 10 * PT * 1.03;
  const lh = 10 * PT * 1.45;
  doc.setTextColor(...DARK); doc.setFontSize(10);
  doc.setFont('OpenSans', 'semibold');
  doc.text('Account details for payment:', x, cy); cy += lh + 0.8;
  doc.setFont('OpenSans', 'normal');
  for (const [label, val] of [
    ['Account Holder:', b.account_holder], ['Account Number:', b.account_number],
    ['IFSC:', b.ifsc], ['Branch:', b.branch], ['Account Type:', b.account_type],
  ]) { doc.text(`${label} ${val || ''}`, x, cy); cy += lh; }
}

function signoffAt(doc, x, y, w, name) {
  let cy = y + 10.5 * PT * 1.03;
  doc.setTextColor(...DARK); doc.setFont('OpenSans', 'normal'); doc.setFontSize(10.5);
  doc.text('Sincerely,', x + w, cy, { align: 'right' }); cy += 10.5 * PT * 1.45 + 0.5;
  doc.setFont('OpenSans', 'semibold');
  doc.text(String(name || ''), x + w, cy, { align: 'right' });
}

function feeTableAt(doc, x, y, w, proposal, cfg = {}) {
  const labels = { desc: 'DESCRIPTION', qty: 'QTY.', rate: 'RATE', price: 'PRICE', ...(cfg.labels || {}) };
  const showQty = cfg.show_qty !== false;
  const showRate = cfg.show_rate !== false;
  const head = [[labels.desc, ...(showQty ? [labels.qty] : []), ...(showRate ? [labels.rate] : []), labels.price]];
  const n = head[0].length;
  const body = (proposal.fee_table || []).map(r => [
    r.description || '',
    ...(showQty ? [r.qty != null && r.qty !== '' ? String(r.qty) : ''] : []),
    ...(showRate ? [r.rate != null && r.rate !== '' ? String(r.rate) : 'L.S.'] : []),
    money(r.price),
  ]);
  // Totals rows: filler cells + label + amount; the first totals row draws a
  // full-width divider (every cell), matching the gold header rule.
  const dividerStyles = { lineWidth: { top: 0.4 }, lineColor: [220, 220, 220] };
  const totalsRow = (label, val, { bold = false, divider = false } = {}) => {
    const cellStyle = { ...(bold ? { fontStyle: 'bold' } : {}), ...(divider ? dividerStyles : {}) };
    const cells = [];
    for (let i = 0; i < n - 2; i++) cells.push(divider ? { content: '', styles: { ...dividerStyles } } : '');
    cells.push({ content: label, styles: { ...cellStyle } });
    cells.push({ content: val, styles: { ...cellStyle } });
    return cells;
  };
  if (proposal.gst_included) {
    body.push(totalsRow('Subtotal', money(proposal.subtotal), { divider: true }));
    body.push(totalsRow(`GST (${proposal.gst_rate || 18}%)`, money(proposal.gst_amount)));
    body.push(totalsRow('Total', money(proposal.total_amount), { bold: true }));
  } else {
    // No GST → subtotal equals total; show a single Total line.
    body.push(totalsRow('Total', money(proposal.total_amount), { bold: true, divider: true }));
  }
  const cw = { qty: 20, rate: 20, price: 26, ...(cfg.col_widths || {}) };
  const rowPad = cfg.row_pad != null ? Number(cfg.row_pad) : 1.2;
  const columnStyles = { 0: { cellWidth: Math.max(20, w - cw.price - (showQty ? cw.qty : 0) - (showRate ? cw.rate : 0)) } };
  let ci = 1;
  if (showQty) columnStyles[ci++] = { cellWidth: cw.qty };
  if (showRate) columnStyles[ci++] = { cellWidth: cw.rate };
  columnStyles[ci] = { cellWidth: cw.price, halign: 'right' };
  autoTable(doc, {
    startY: y,
    margin: { left: x, right: PAGE_W - (x + w), bottom: 8 },
    head,
    body,
    theme: 'plain',
    styles: { font: 'OpenSans', fontSize: 10, cellPadding: { top: rowPad, bottom: rowPad, left: 0, right: 2 }, textColor: DARK, valign: 'top' },
    headStyles: { textColor: GOLD, fontStyle: 'bold', fontSize: 11, cellPadding: { top: 1, bottom: 2, left: 0, right: 2 }, lineWidth: { bottom: 0.7 }, lineColor: GOLD },
    columnStyles,
  });
}

function renderBoxes(doc, boxes, pageCount, { proposal, bank, showBank, branding }) {
  const maxPage = Math.max((Number(pageCount) || 1) - 1, ...boxes.map(b => Number(b.page) || 0));
  for (let p = 0; p <= maxPage; p++) {
    if (p > 0) { doc.addPage(); drawChrome(doc); }
    const items = boxes
      .filter(b => (Number(b.page) || 0) === p)
      .sort((a, b2) => (a.y - b2.y) || (a.x - b2.x));
    for (const b of items) {
      const x = Number(b.x) || 0, y = Number(b.y) || 0, w = Math.max(20, Number(b.w) || 124);
      switch (b.type) {
        case 'text': {
          const cur = { y, ensure() {}, space(h) { this.y += h; } };
          renderRichHtml(doc, cur, b.html, { x, width: w, anchorTop: true });
          break;
        }
        case 'heading': headingAt(doc, b.text, x, y, w); break;
        case 'line': {
          const lineColors = { gold: GOLD, grey: GREY_LINE, dark: DARK, teal: [58, 140, 140] };
          doc.setDrawColor(...(lineColors[b.color] || GOLD));
          doc.setLineWidth(Number(b.weight) || 0.7);
          doc.line(x, y + 1, x + w, y + 1);
          break;
        }
        case 'table': feeTableAt(doc, x, y, w, proposal, b); break;
        case 'bank': bankAt(doc, x, y, showBank ? bank : {}); break;
        case 'signoff': signoffAt(doc, x, y, w, proposal.sign_name || branding?.accountName); break;
        default: break;
      }
    }
  }
}

// Render the page-builder block list in order; mirrors the on-canvas editor.
function renderBlocks(doc, flow, blocks, { proposal, bank, showBank, branding }) {
  for (const b of blocks) {
    switch (b.type) {
      case 'heading': flow.heading(b.text); break;
      case 'rich': renderRichHtml(doc, flow, b.html); break;
      case 'para': flow.para(b.text, { gap: 4 }); break;
      case 'bullets': flow.bullets((b.items || []).filter(Boolean)); flow.space(3); break;
      case 'line': flow.line(b.color === 'grey' ? GREY_LINE : GOLD); break;
      case 'spacer': flow.space(Number(b.h) || 6); break;
      case 'pagebreak': flow.newPage(); break;
      case 'table':
        feeTable(doc, flow, proposal.fee_table, {
          total: proposal.total_amount, gstIncluded: proposal.gst_included, gstRate: proposal.gst_rate,
          gstAmount: proposal.gst_amount, subtotal: proposal.subtotal,
        });
        break;
      case 'bank': accountBlock(doc, flow, showBank ? bank : {}); break;
      case 'signoff': signoff(doc, flow, proposal.sign_name, branding?.accountName); flow.space(8); break;
      default: break;
    }
  }
}

export async function generateProposalPdf({ proposal, branding, letterhead, bank, showBank }) {
  applyMargins(proposal.margins);
  const doc = newLetterheadDoc();
  const flow = makeFlow(doc);

  // Free-box canvas path: boxes carry their own positions, including the
  // title/date/To header (seeded as text boxes) — no fixed header.
  if (Array.isArray(proposal.blocks) && proposal.blocks.some(b => b.x != null)) {
    renderBoxes(doc, proposal.blocks.filter(b => b.x != null), proposal.page_count, { proposal, bank, showBank, branding });
    return doc;
  }

  header(doc, flow, { title: proposal.doc_title || 'Design Proposal', date: proposal.date, toName: proposal.client_name, re: proposal.re });

  // Older flow-block path: the block list is the document body.
  if (Array.isArray(proposal.blocks) && proposal.blocks.length) {
    renderBlocks(doc, flow, proposal.blocks, { proposal, bank, showBank, branding });
    return doc;
  }

  feeTable(doc, flow, proposal.fee_table, {
    total: proposal.total_amount, gstIncluded: proposal.gst_included, gstRate: proposal.gst_rate,
    gstAmount: proposal.gst_amount, subtotal: proposal.subtotal,
  });

  if (proposal.quotation_note) flow.para(proposal.quotation_note, { gap: 6 });

  const scope = (proposal.scope_items || []).filter(s => s.included !== false).map(s => s.text || s).filter(Boolean);
  if (scope.length) { flow.heading(proposal.scope_heading || 'Scope of Work - Design Consultation'); flow.bullets(scope); flow.space(3); }

  const terms = (proposal.terms || []).filter(t => (typeof t === 'string' ? true : t.included !== false)).map(t => (typeof t === 'string' ? t : t.text)).filter(Boolean);
  if (terms.length) {
    flow.heading('Scope of Work / Terms & Conditions');
    flow.para('Terms & conditions are as follows:', { gap: 3 });
    flow.bullets(terms);
    flow.space(3);
  }

  accountBlock(doc, flow, showBank ? bank : {});
  if (proposal.closing_note) flow.para(proposal.closing_note, { gap: 8 });
  signoff(doc, flow, proposal.sign_name, branding?.accountName);
  return doc;
}

export async function generateInvoicePdf({ invoice, branding, letterhead, bank }) {
  applyMargins(null);
  const doc = newLetterheadDoc();
  const flow = makeFlow(doc);

  // Free-box canvas path: boxes carry their own positions (incl. the header);
  // invoices always show the bank details.
  if (Array.isArray(invoice.blocks) && invoice.blocks.some(b => b.x != null)) {
    renderBoxes(doc, invoice.blocks.filter(b => b.x != null), invoice.page_count, { proposal: invoice, bank, showBank: true, branding });
    return doc;
  }

  header(doc, flow, {
    title: 'Invoice',
    date: invoice.date,
    toName: invoice.client_name,
    re: invoice.invoice_number ? `Invoice No: ${invoice.invoice_number}` : (invoice.re || ''),
  });

  feeTable(doc, flow, invoice.fee_table, {
    total: invoice.total_amount, gstIncluded: invoice.gst_included, gstRate: invoice.gst_rate,
    gstAmount: invoice.gst_amount, subtotal: invoice.subtotal,
  });

  if (invoice.note) flow.para(invoice.note, { gap: 6 });

  // Invoice always carries the bank details (no scope, no terms).
  accountBlock(doc, flow, bank || {}, { heading: 'Payment / Bank details:' });
  flow.para(invoice.closing_note || 'Thank you for your business.', { gap: 8 });
  signoff(doc, flow, invoice.sign_name, branding?.accountName);
  return doc;
}
